package uz.mint.frontend.components.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import uz.mint.frontend.PageGutter
import uz.mint.frontend.ShellStyle
import uz.mint.frontend.components.widgets.ChatIcon
import uz.mint.frontend.components.widgets.DollarIcon
import uz.mint.frontend.components.widgets.PhoneIcon
import uz.mint.frontend.components.widgets.ZapIcon
import uz.mint.frontend.i18n.SiteStrings
import uz.mint.frontend.toSitePalette
import uz.mint.frontend.alignItems
import uz.mint.frontend.alignSelf
import uz.mint.frontend.flexWrapCss
import uz.mint.frontend.gridTemplateColumnsCss
import uz.mint.frontend.hairlineCell
import uz.mint.frontend.justifyContent
import uz.mint.frontend.marginInlineAuto
import uz.mint.frontend.placeItems
import uz.mint.frontend.widthCss

@Composable
fun Advantages(strings: SiteStrings) {
    val palette = ColorMode.current.toSitePalette()

    Div(
        Modifier
            .fillMaxWidth()
            .id(ADVANTAGES_ANCHOR)
            .backgroundColor(palette.paper2)
            .borderTop(2.px, LineStyle.Solid, palette.rule)
            .borderBottom(2.px, LineStyle.Solid, palette.rule)
            .toAttrs()
    ) {
        Div(ShellStyle.toModifier().padding(topBottom = clamp(44.px, 4.6.vw, 76.px), leftRight = PageGutter).toAttrs()) {
            H2(
                Modifier
                    .fontSize(clamp(28.px, 3.vw, 44.px)).lineHeight(1.02)
                    .letterSpacing((-0.035).em).fontWeight(FontWeight.ExtraBold)
                    .margin(bottom = clamp(24.px, 2.6.vw, 36.px)).maxWidth(20.em)
                    .toAttrs()
            ) { SpanText(strings.advTitle) }

            Div(
                Modifier
                    .fillMaxWidth()
                    // Card-filled, so a row of four wrapping to three does not leave a slab
                    // of rule colour where the fourth card would have been.
                    .backgroundColor(palette.card).padding(2.px)
                    .display(DisplayStyle.Grid).gap(2.px)
                    .gridTemplateColumnsCss("repeat(auto-fit,minmax(min(100%,270px),1fr))")
                    .toAttrs()
            ) {
                // Icons are positional in the design — one per advantage, in order.
                val icons: List<@Composable (Color) -> Unit> = listOf(
                    { c -> ZapIcon(color = c) },
                    { c -> PhoneIcon(color = c) },
                    { c -> DollarIcon(color = c) },
                    { c -> ChatIcon(color = c) },
                )
                strings.advantages.forEachIndexed { index, advantage ->
                    Card(advantage, icons.getOrNull(index), palette.card, palette.icon, palette.ink2, palette.rule)
                }
            }
        }
    }
}

@Composable
private fun Card(
    advantage: SiteStrings.Advantage,
    icon: (@Composable (Color) -> Unit)?,
    card: Color,
    iconColor: Color,
    ink2: Color,
    rule: Color,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(card)
            .hairlineCell(rule)
            .padding(top = 26.px, leftRight = 24.px, bottom = 28.px)
            .gap(16.px)
    ) {
        icon?.invoke(iconColor)
        H3(
            Modifier.fontSize(19.px).fontWeight(FontWeight.ExtraBold).letterSpacing((-0.02).em).toAttrs()
        ) { SpanText(advantage.title) }
        P(Modifier.fontSize(15.px).color(ink2).lineHeight(1.5).toAttrs()) { SpanText(advantage.body) }
    }
}
